import type { Tracker, Properties } from '../track';
import { getSystemInfo } from '@doxyme/ui/hooks';
import { RealtimeRole } from '@doxyme/realtime';
import { MembershipType } from '../../../services';

type SegmentOpts = {
  context?: {
    ip?: string;
    platform: string;
    browser: string;
    os: string;
    browser_version: string;
    os_version: string;
  };
};

type SegmentTracker = Tracker & {
  shutdownIntercom: () => void;
  rebootIntercom: () => void;
};

let latestIdentifyProperties: {
  id: number;
  traits: Record<string, unknown>;
  hash: string;
} | null = null;

const toSnakeCaseKeys = (
  obj: Record<string, unknown>,
): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      const snakeKey = key.replace(/[A-Z]/gu, (letter, index) =>
        index === 0 ? letter.toLowerCase() : `_${letter.toLowerCase()}`,
      );
      return [snakeKey, value];
    }),
  );
};

const buildSegmentOpts = (properties?: Properties): SegmentOpts => {
  const userType = properties?.['userType'] ?? RealtimeRole.Patient;
  const {
    deviceType: platform,
    browserVersion,
    osVersion,
    ...otherInfo
  } = getSystemInfo();
  return {
    context: {
      ...(userType === RealtimeRole.Patient && {
        ip: '0.0.0.0',
      }),
      platform,
      ...otherInfo,
      browser_version: browserVersion,
      os_version: osVersion,
    },
  };
};

const SegmentTracker: SegmentTracker = {
  id: 'segment',

  identify: (user, institution, latestPurchase, rooms) => {
    const { analytics } = window;
    const traits: Record<string, unknown> = {};

    if (rooms) {
      const room = rooms?.find(
        (r) => r.node.membershipType === MembershipType.Owner,
      )?.node;
      const origin =
        typeof window === 'undefined' ? '' : window.location.origin;

      if (room) {
        traits[
          'track_js_patient_errors'
        ] = `https://my.trackjs.com/search?query=${room.slug}`;
        traits['roomSlug'] = `${origin}/${room.slug ?? ''}`;
      }
    }

    if (institution) {
      traits['company'] = {
        name: institution.name,
        company_id: institution.id,
        isActive: true,
        'V2 Clinic': true,
      };
    }

    if (user) {
      const { id, hash, title, firstName, lastName, email, created } = user;
      const trackJSUrl = `https://my.trackjs.com/users/filtered?userid=${user.id}`;
      traits['track_js_url'] = trackJSUrl;
      traits['name'] = [title === 'Other' ? '' : title, firstName, lastName]
        .filter(Boolean)
        .join(' ');
      traits['email'] = email;
      traits['created_at'] = created;
      traits['plan'] = latestPurchase?.planId ?? 'Free';

      analytics.identify(id, traits, {
        Intercom: {
          user_hash: hash,
        },
      });

      latestIdentifyProperties = {
        id,
        traits,
        hash,
      };
    } else {
      analytics.identify(undefined, {});
      latestIdentifyProperties = null;
    }
  },

  track: async (event, properties) => {
    return new Promise((resolve) => {
      const { analytics } = window;
      const newDate = new Date();

      analytics.track(
        event,
        {
          date: `${newDate.toDateString()} ${newDate.toTimeString()}`,
          is_apollo: true,
          ...toSnakeCaseKeys(properties ?? {}),
        },
        buildSegmentOpts(properties),
        resolve,
      );
    });
  },

  shutdownIntercom: () => {
    window.Intercom?.('shutdown');
  },

  rebootIntercom: () => {
    const { analytics } = window;

    if (latestIdentifyProperties !== null) {
      const { id, traits, hash } = latestIdentifyProperties;
      analytics?.identify(id, traits, {
        Intercom: {
          user_hash: hash,
        },
      });
    } else {
      analytics?.identify(undefined, {});
    }
  },

  reset: () => {
    const { analytics } = window;
    analytics?.reset?.();
    latestIdentifyProperties = null;
  },
};

export default SegmentTracker;
